import type { Side } from './espn'

/*
 * Scoring.
 *
 * Every number in this file is derived and none of it is ever written down.
 * That is not tidiness — it is the whole reason the boys get to own the rules.
 * Because a point is computed from (pick, game, result, ruleset) at read time,
 * turning the underdog bonus on in October rescores the entire season, and no
 * migration, no backfill and no stored total has to be corrected.
 *
 * The moment any of this is cached in a column, that guarantee is gone.
 */

export interface Ruleset {
  pointsCorrect: number
  underdogBonus: number
  /**
   * What an absent pick is worth. See `points` — nothing is written for it.
   *
   * 'lowest' scores exactly like 'favorite' HERE, one game at a time, and is
   * then capped per week by `applyMissingCap`. It has to work that way: a
   * ceiling set by the worst player who turned up is a fact about the whole
   * board and every other person on it, and this function sees one pick.
   */
  missingPick: 'favorite' | 'zero' | 'lowest'
  tieCredits: 'both' | 'neither'
  voidCredits: 'none' | 'all'
}

/**
 * Sane defaults, and deliberately dull ones.
 *
 * The bonus ships off so the family starts with the simplest game that works
 * and turns knobs once they have an opinion. `missingPick: 'favorite'` is the
 * exception — it ships on, because a missed week scored as zero puts the
 * eight-year-old out of the season by week three, and a player who cannot win
 * stops opening the app. The favorite is the least interesting pick available,
 * so it keeps him alive without rewarding silence.
 */
export const DEFAULT_RULES: Ruleset = {
  pointsCorrect: 1,
  underdogBonus: 0,
  missingPick: 'favorite',
  tieCredits: 'both',
  voidCredits: 'none',
}

/** Only what scoring needs off the frozen board row. */
export interface ScoredGame {
  favSide: Side | null
  spread: number | null
}

export interface Outcome {
  winnerSide: Side | 'tie' | 'void' | null
  final: boolean
}

/**
 * What one pick on one game is worth.
 *
 * `pick` is null when the player never picked. There is no autofilled row in
 * the database to represent that — an absent pick is a branch here instead.
 * Storing it would make one stored derivation among facts, and flipping
 * `missingPick` later would leave those rows behind, so that single knob would
 * be the only one that failed to rescore.
 */
export function points(
  pick: Side | null,
  game: ScoredGame,
  result: Outcome,
  rules: Ruleset,
): number {
  // A team leading at halftime has not won anything.
  if (!result.final || result.winnerSide === null) return 0

  if (result.winnerSide === 'void') {
    return rules.voidCredits === 'all' ? rules.pointsCorrect : 0
  }

  if (result.winnerSide === 'tie') {
    return rules.tieCredits === 'both' ? rules.pointsCorrect : 0
  }

  if (pick === null) {
    /*
     * The assumed favorite never earns the underdog bonus — by construction it
     * is the favorite — so this returns early rather than falling through.
     * With no favorite to assume (a pick-em, or a game that never got a line)
     * there is nothing to stand in for the player and it scores zero.
     */
    if (rules.missingPick === 'zero' || game.favSide === null) return 0
    // 'favorite' and 'lowest' agree here; 'lowest' is trimmed a week at a time.
    return result.winnerSide === game.favSide ? rules.pointsCorrect : 0
  }

  if (pick !== result.winnerSide) return 0

  /*
   * The underdog is the side that is NOT the favorite, and only when there is
   * a favorite at all. A pick-em is spread 0 with no favorite; an unpriced game
   * has no spread. In both cases nobody is a dog and the bonus cannot apply.
   */
  const wasUnderdog = game.favSide !== null && pick !== game.favSide
  return rules.pointsCorrect + (wasUnderdog ? rules.underdogBonus : 0)
}

/** A player's total across one board. Derived, never stored. */
export function weekTotals(
  board: readonly { game: ScoredGame; result: Outcome }[],
  picks: readonly (Side | null)[],
  rules: Ruleset,
): number {
  return board.reduce((sum, row, i) => sum + points(picks[i] ?? null, row.game, row.result, rules), 0)
}

/**
 * The ceiling on a week nobody picked.
 *
 * `missingPick: 'favorite'` exists so a missed week does not put the youngest
 * player out of the season, and it works - but taking the chalk is a genuinely
 * decent strategy, so in a week where the favourites hold it hands somebody who
 * never opened the app a respectable score. Beating a player who turned up and
 * took a dog is the one outcome the family will not accept, and it is the only
 * thing this corrects.
 *
 * A CEILING, NEVER A FLOOR. It can only pull a blank week down to the worst
 * player who actually picked. If the favourites had a bad week and earned less
 * than that, the blank week keeps the smaller number - the rule has no opinion
 * about anybody it is not already ahead of.
 *
 * Two no-shows both measure against the PICKERS, never against each other, so
 * they tie. A cascade would make the second one's score depend on the first,
 * which is not a rule anybody could explain at a kitchen table.
 *
 * Takes raw totals rather than a board because the two callers count in
 * different directions - the season endpoint walks every game and buckets by
 * week, This Week walks one board - and both can arrive here with an array.
 * The RULE lives in one place; the arithmetic that feeds it does not have to.
 *
 * `raw` and `pickedAny` are parallel, one entry per player.
 */
export function applyMissingCap(
  raw: readonly number[],
  pickedAny: readonly boolean[],
  rules: Ruleset,
): number[] {
  if (rules.missingPick !== 'lowest') return [...raw]

  const played = raw.filter((_, i) => pickedAny[i] === true)
  // Nobody turned up, so there is no floor to measure against and nothing
  // to say about who deserved what.
  if (played.length === 0) return [...raw]

  const cap = Math.min(...played)
  return raw.map((n, i) => (pickedAny[i] === true ? n : Math.min(n, cap)))
}

/**
 * One board, every player, capped.
 *
 * The reason this exists rather than each screen running its own reduce: the
 * cap is not a per-player fact, so remembering to apply it is a step a caller
 * can forget. One that forgets does not crash - it quietly disagrees, and the
 * way you find out is This Week and a recap pasted into the family thread
 * naming different winners.
 *
 * `pickedAny` IS A PARAMETER, NOT DERIVED FROM `picksByPlayer`, and this is the
 * important line in the file. Before a board locks the server sends every other
 * player's picks as null (shared/visibility.ts) - that is the whole point of
 * the filter. A caller inferring "did they pick?" from the array it was handed
 * would decide that eleven of twelve people were no-shows and cap the entire
 * family down to the lowest score on the board. Whether somebody picked is a
 * count the server sends unfiltered, precisely because it says how many and
 * never which side.
 */
export function weekScores(
  board: readonly { game: ScoredGame; result: Outcome }[],
  picksByPlayer: readonly (readonly (Side | null)[])[],
  pickedAny: readonly boolean[],
  rules: Ruleset,
): number[] {
  const raw = picksByPlayer.map((picks) => weekTotals(board, picks, rules))
  return applyMissingCap(raw, pickedAny, rules)
}

/**
 * A ruleset and the week it came into force.
 *
 * Scoring is derived at read time, so a knob turned in October used to rescore
 * September with it - the guarantee and the trap in the same sentence. Scott
 * asked on 2026-09-17 for missed picks to score zero from week 3 onward with
 * weeks 1 and 2 left as they were played, and that is only expressible if a
 * rule change carries a date.
 *
 * So the rules table holds a row per change rather than one row per season,
 * and history stops moving under the family: what happened in week 1 is scored
 * by the rules week 1 was played under, for good.
 */
export interface VersionedRules extends Ruleset {
  /** The first week number this ruleset governs. */
  fromWeek: number
}

/**
 * The ruleset a given week is scored by: the newest one in force by then.
 *
 * Falls back to the defaults for a week no version covers - a season with no
 * rules row at all, or a week before the earliest change - which is the same
 * answer the single-row version gave when the row was missing.
 */
export function rulesForWeek(
  versions: readonly VersionedRules[],
  weekNumber: number,
): Ruleset {
  let best: VersionedRules | null = null
  for (const v of versions) {
    if (v.fromWeek > weekNumber) continue
    if (best === null || v.fromWeek > best.fromWeek) best = v
  }
  if (best === null) return DEFAULT_RULES
  const { fromWeek: _ignored, ...rules } = best
  return rules
}
